import Vue from 'vue';
import Vuex from 'vuex';

// import VueCookies from 'vue-cookies';

import { copy, overlay } from '@/utils';

Vue.use(Vuex);
// Vue.use(VueCookies);

const vue = new Vue();

const emptyImage = {
  key: '',
  path: '',
};

const emptyDesign = {
  name: '',
  designcode: '',
  image: copy(emptyImage),
  color: '',
  relativePosition: {
    centerX: 0,
    centerY: 0,
    width: 1,
    height: 1,
    rot: 0,
  },
};

export default new Vuex.Store({
  state: {
    /* eslint-disable no-multi-spaces */
    designs: {               // mutiple saved designs,
      design: copy(emptyDesign),    // with default named 'design'
    },
    currentDesign: 'design', // which of the 'designs' keys we're using now; default name
    lastSavedDesigns: {},    // state of the 'designs' last it was sent to server (to track changes)
    leadProduct: {},         // which is the "main" product
    product: {},             // the active 'product'
    products: {},            // possible 'product' choices
    config: {},              // mostly configured endpoints to the server
    imageToSelect: {},       // the selected image in the image chooser
    savedDesigns: null,      // a list of designs on server for Load; null means "unset"
    designToSelect: '',      // the selected design in the design chooser
    guideVisible: true,      // if the guide overlay is shown
    /* eslint-enable no-multi-spaces */
  },
  getters: {
    /* the current design, as per 'currentDesign' */
    design(state) {
      return state.designs[state.currentDesign];
    },
    /* The product object associated with the lead product
     * NOTE: different from the state param with the same name
     */
    leadProduct(state) {
      return state.products[state.leadProduct];
    },
    /* The product associated with the lead product */
    leadDesign(state) {
      return state.designs[state.leadProduct];
    },
    /* mapping of product.sku to product.key */
    skuMap(state) {
      const skusToProductKey = {};
      const productKeys = Object.keys(state.products);
      productKeys.forEach((k) => { skusToProductKey[state.products[k].id] = k; });
      return skusToProductKey;
    },
  },
  mutations: {
    setProducts(state, products) {
      console.log('setProducts:', products);
      state.products = products;
      const productKeys = Object.keys(products);
      // pick the first product as the default 'state.product'
      const productKey = productKeys[0]; /* eslint-disable-line prefer-destructuring */
      state.product = products[productKey];
      // // store product key in product in case we need it
      // state.product.key = productKey;
      // set a default lead product, in case it's not called separately
      state.leadProduct = productKey;

      // set designs to match; throws away any design with no matching product
      const designs = {};
      for (let i = 0; i < productKeys.length; i += 1) {
        const k = productKeys[i];
        // store product key in product in case we need it
        state.products[k].key = k;

        // create designs based on the key; keep existing if we have it, otherwise empty
        if (state.designs[k]) {
          designs[k] = state.designs[k];
        } else {
          designs[k] = copy(emptyDesign);
        }
      }
      state.designs = designs;
      state.currentDesign = productKey;
    },
    /* provide name of product in 'products' to be state.product */
    changeCurrentProduct(state, productkey) {
      state.product = state.products[productkey];
      // store product key in product in case we need it
      state.product.key = productkey;
    },
    setLeadProduct(state, productkey) {
      state.leadProduct = productkey;
    },
    /* swap out the whole current 'product' */
    setProduct(state, product) {
      const currentkey = state.product.key;
      const newproduct = copy(product);
      newproduct.key = currentkey;
      state.products[currentkey] = newproduct;
      state.product = newproduct;
    },
    // setProductProps(state, params) {
    //   Object.keys(params).forEach((key) => {
    //     Vue.set(state.product, key, params[key]);
    //   });
    // },
    setProductOptions(state, options) {
      // change the 'option' on the current lead product
      Object.keys(options).forEach((key) => {
        // set the product option-controlling property
        const val = options[key];
        Vue.set(state.product, key, val);
        // TODO: this is not updated for multiple products, but seems to work anyway
        // because of the below

        // set other product properties matching that value
        state.product.options.forEach((option) => {
          if (option.name === key) { // there's a set of options we changed
            const matching = option.choices.filter((choice) => choice.name === val);
            if (matching) {
              const matchedChoice = matching[0];
              const newProduct = overlay(state.product, matchedChoice.overrides);
              this.commit('setProduct', newProduct);
            }
          }
        });
      });
    },
    setConfig(state, config) {
      state.config = config;
    },

    changeCurrentDesign(state, designKey) {
      state.currentDesign = designKey;

      // initialize design by that name if it doesn't exist yet
      if (!state.designs.hasOwnProperty(designKey)) { // eslint-disable-line no-prototype-builtins
        Vue.set(state.designs, designKey, copy(emptyDesign));
        // state.designs[designKey] = copy(emptyDesign);
      }
    },
    setDesign(state, designparams) {
      Object.keys(designparams).forEach((key) => {
        // console.log('setDesign k/v:', key, designparams[key]);
        Vue.set(state.designs[state.currentDesign], key, designparams[key]);
      });
      // console.log('setDesign', designparams, state.design);
    },
    /* like setDesign, but pick any of the designs in 'designs'.
     * Pass in 'designkey' and 'designparams'.
     * By default, the "lead product" design is used if no 'designkey'
    */
    setSpecificDesign(state, payload) {
      let { designkey } = payload;
      const { designparams } = payload;
      if (!designkey) {
        designkey = state.leadProduct;
      }
      // console.log('setSpecificDesign()', designkey, designparams);
      Object.keys(designparams).forEach((key) => {
        // console.log('setDesign k/v:', key, designparams[key]);
        Vue.set(state.designs[designkey], key, designparams[key]);
      });
    },
    clearDesigns(state) {
      const emptyDesigns = {};
      Object.keys(state.designs).forEach((designkey) => {
        emptyDesigns[designkey] = copy(emptyDesign);
      });
      state.designs = emptyDesigns;
      // there is a '$cookies.remove' but it doesn't seem to work reliably
      vue.$cookies.set(state.config.lastDesignCookie, '',
        Infinity, window.location.pathname);
    },

    setImageToSelect(state, { image, bucket }) {
      Vue.set(state.imageToSelect, bucket, image);
    },
    clearImageToSelect(state, bucket) {
      Vue.set(state.imageToSelect, bucket, copy(emptyImage));
    },
    clearImageToSelectAll(state) {
      state.imageToSelect = {};
    },

    setSavedDesigns(state, designlist) {
      state.savedDesigns = designlist;
    },
    clearSavedDesigns(state) {
      state.savedDesigns = null;
    },

    setDesignToSelect(state, designcode) {
      state.designToSelect = designcode;
    },
    clearDesignToSelect(state) {
      state.designToSelect = '';
    },

    toggleGuide(state) {
      state.guideVisible = !state.guideVisible;
    },

    updateLastSavedDesigns(state) {
      state.lastSavedDesigns = copy(state.designs);
      // note the design to load on page reload by saving in cookie
      // we use the current design code
      vue.$cookies.set(state.config.lastDesignCookie, state.designs[state.leadProduct].designcode,
        Infinity, window.location.pathname);
    },
  },
  actions: {
  },
  modules: {
  },
});
